import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { uploadContent } from "../api";

const BackgroundUploadContext = createContext({
  tasks: [],
  startContentUpload: async () => {},
  dismissUpload: () => {},
});

const clampProgress = (value) => Math.max(0, Math.min(100, Math.round(Number(value) || 0)));

const uploadStatusLabel = (task) => {
  if (task.status === "completed") return "Uploaded";
  if (task.status === "failed") return "Upload failed";
  if (task.status === "processing") return "Processing asset";
  return `Uploading ${clampProgress(task.progress)}%`;
};

export function BackgroundUploadProvider({ children }) {
  const [tasks, setTasks] = useState([]);
  const cleanupTimersRef = useRef(new Map());

  const updateTask = useCallback((taskId, patch) => {
    setTasks((current) => current.map((task) => (task.id === taskId ? { ...task, ...patch } : task)));
  }, []);

  const dismissUpload = useCallback((taskId) => {
    const timer = cleanupTimersRef.current.get(taskId);
    if (timer) {
      window.clearTimeout(timer);
      cleanupTimersRef.current.delete(taskId);
    }
    setTasks((current) => current.filter((task) => task.id !== taskId));
  }, []);

  const scheduleDismiss = useCallback(
    (taskId) => {
      const existing = cleanupTimersRef.current.get(taskId);
      if (existing) window.clearTimeout(existing);
      const timer = window.setTimeout(() => dismissUpload(taskId), 6500);
      cleanupTimersRef.current.set(taskId, timer);
    },
    [dismissUpload]
  );

  useEffect(() => {
    return () => {
      cleanupTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      cleanupTimersRef.current.clear();
    };
  }, []);

  const startContentUpload = useCallback(
    ({
      file,
      name,
      duration = 10,
      scope = "workspace",
      folderId = null,
      onProgress,
      onSuccess,
      onError,
      onSettled,
    } = {}) => {
      if (!file) {
        return Promise.reject(new Error("No file selected"));
      }

      const taskId = `upload_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const fileName = String(name || file.name || "asset");

      setTasks((current) => [
        {
          id: taskId,
          fileName,
          progress: 0,
          status: "uploading",
        },
        ...current,
      ]);

      return uploadContent(file, fileName, duration, scope, folderId, {
        onUploadProgress: (event) => {
          const total = Number(event.total || file.size || 0);
          const progress = total > 0 ? clampProgress((Number(event.loaded || 0) / total) * 100) : 0;
          const status = progress >= 100 ? "processing" : "uploading";
          const nextProgress = { fileName, progress, status };
          updateTask(taskId, nextProgress);
          onProgress?.(nextProgress, event);
        },
      })
        .then((response) => {
          updateTask(taskId, { progress: 100, status: "completed" });
          onSuccess?.(response);
          return response;
        })
        .catch((error) => {
          updateTask(taskId, {
            status: "failed",
            errorMessage: error?.response?.data?.detail || error?.message || "Upload failed",
          });
          onError?.(error);
          throw error;
        })
        .finally(() => {
          onSettled?.();
          scheduleDismiss(taskId);
        });
    },
    [scheduleDismiss, updateTask]
  );

  const visibleTasks = tasks.slice(0, 3);
  const activeCount = tasks.filter((task) => task.status === "uploading" || task.status === "processing").length;

  const value = useMemo(
    () => ({
      tasks,
      startContentUpload,
      dismissUpload,
    }),
    [dismissUpload, startContentUpload, tasks]
  );

  return (
    <BackgroundUploadContext.Provider value={value}>
      {children}
      {visibleTasks.length ? (
        <div className="background-upload-indicator" role="status" aria-live="polite">
          <div className="background-upload-head">
            <strong>{activeCount ? "Uploads running" : "Uploads complete"}</strong>
            <span>{tasks.length} {tasks.length === 1 ? "file" : "files"}</span>
          </div>
          <div className="background-upload-list">
            {visibleTasks.map((task) => (
              <div key={task.id} className={`background-upload-item is-${task.status}`}>
                <div className="background-upload-row">
                  <span className="background-upload-name" title={task.fileName}>{task.fileName}</span>
                  <button
                    type="button"
                    className="background-upload-dismiss"
                    onClick={() => dismissUpload(task.id)}
                    aria-label={`Dismiss upload status for ${task.fileName}`}
                  >
                    x
                  </button>
                </div>
                <div className="background-upload-meta">
                  {task.errorMessage || uploadStatusLabel(task)}
                </div>
                <div className="background-upload-track" aria-hidden="true">
                  <span style={{ width: `${clampProgress(task.progress)}%` }}></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </BackgroundUploadContext.Provider>
  );
}

export function useBackgroundUpload() {
  return useContext(BackgroundUploadContext);
}
